import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, User, Heart, Globe, MapPin, ChevronDown, Camera, Grid, UserPlus, QrCode, ShoppingCart, Building, Store, Check, ShoppingBag } from 'lucide-react';
import { categories } from '../data/products';
import { useSupabase } from '../contexts/SupabaseContext';
import { useCart } from '../contexts/CartContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isShipToOpen, setIsShipToOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { totalItems } = useCart();
  const { user, signOut } = useSupabase();

  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: 'AE', name: 'UAE', flag: '🇦🇪' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'CN', name: 'China', flag: '🇨🇳' }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  const selectedCountryData = countries.find(c => c.name === selectedCountry) || countries[0];
  const selectedLanguageData = languages.find(l => l.name === selectedLanguage) || languages[0];

  // Sample search suggestions
  const searchSuggestions = [
    { type: 'category', name: 'Garments & Apparel', count: 125 },
    { type: 'category', name: 'Jute & Natural Fiber', count: 95 },
    { type: 'product', name: 'Cotton T-Shirts', count: 35 },
    { type: 'product', name: 'Jute Bags', count: 30 },
    { type: 'supplier', name: 'Bengal Textiles Ltd.', verified: true },
    { type: 'supplier', name: 'Eco Jute Co.', verified: true },
  ];

  // Filter suggestions based on search query
  const filteredSuggestions = searchQuery.length > 0
    ? searchSuggestions.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchSuggestions;

  const handleImageSearch = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        console.log('Image selected for search:', file.name);
        alert('Image search feature coming soon! Selected: ' + file.name);
      }
    };
    fileInput.click();
  };

  // Handle search form submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchSuggestions(false);
    }
  };

  // Handle user sign out
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      // Always redirect to home page regardless of sign out success/failure
      navigate('/');
    }
  };

  // Close search suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchSuggestions(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchRef, userMenuRef]);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-madeinbd-gradient text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-row justify-between items-center text-xs sm:text-sm">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Ship To Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsShipToOpen(!isShipToOpen)}
                  className="flex items-center space-x-1 hover:text-orange-200 transition-colors"
                >
                  <MapPin className="h-4 w-4" />
                  <span className="hidden sm:inline">Ship to</span>
                  <span>{selectedCountryData.flag}</span>
                  <ChevronDown className="h-3 w-3 hidden sm:inline" />
                </button>
                
                {isShipToOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
                    <div className="p-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-900">Ship to</h3>
                    </div>
                    <div>
                      {countries.map((country) => (
                        <button
                          key={country.code}
                          onClick={() => {
                            setSelectedCountry(country.name);
                            setIsShipToOpen(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                            selectedCountry === country.name ? 'bg-orange-50 text-orange-700' : 'text-gray-700'
                          }`}
                        >
                          <span className="text-lg">{country.flag}</span>
                          <span className="text-sm">{country.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Language Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center space-x-1 hover:text-orange-200 transition-colors"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">Language</span>
                  <span>{selectedLanguageData.flag}</span>
                  <ChevronDown className="h-3 w-3 hidden sm:inline" />
                </button>
                
                {isLanguageOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
                    <div className="p-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-900">Select Language</h3>
                    </div>
                    <div>
                      {languages.map((language) => (
                        <button
                          key={language.code}
                          onClick={() => {
                            setSelectedLanguage(language.name);
                            setIsLanguageOpen(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
                            selectedLanguage === language.name ? 'bg-orange-50 text-orange-700' : 'text-gray-700'
                          }`}
                        >
                          <span className="text-lg">{language.flag}</span>
                          <span className="text-sm">{language.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/become-exclusive" className="hover:text-orange-200 transition-colors flex items-center">
                <Building className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Become Exclusive</span>
              </Link>
              <a href="/merchant-acquisition" className="hover:text-orange-200 transition-colors flex items-center" target="_self">
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Become Supplier</span>
              </a>
              <Link to="/seller-login" className="hover:text-orange-200 transition-colors flex items-center">
                <Store className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Seller Central</span>
              </Link>
              <Link to="/help" className="hover:text-orange-200 transition-colors hidden sm:block">Help Center</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Made Larger */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="/madeinbd-logo.png" 
              alt="Made in BD" 
              className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain"
              style={{ maxWidth: '280px' }}
            />
          </Link>

          {/* Search Bar - Oval Shape with Image Search */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <div className="relative w-full" ref={searchRef}>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
                  setShowSearchSuggestions(false);
                }
              }}>
                <input
                  type="text"
                  placeholder="Search for products, categories, suppliers..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchSuggestions(true);
                  }}
                  onFocus={() => setShowSearchSuggestions(true)}
                  className="w-full pl-6 pr-24 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                  <button 
                    type="button"
                    onClick={handleImageSearch}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-600 p-2 rounded-full transition-colors"
                    title="Search by image"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <button 
                    type="submit"
                    className="bg-madeinbd-gradient text-white p-2 rounded-full hover:opacity-90 transition-opacity"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </form>

              {/* Search Suggestions */}
              {showSearchSuggestions && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  {searchQuery.length === 0 && (
                    <div className="p-3 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-gray-900">Popular Searches</h3>
                    </div>
                  )}
                  
                  {filteredSuggestions.length > 0 ? (
                    <div>
                      {filteredSuggestions.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            const searchTerm = item.name;
                            setSearchQuery(searchTerm);
                            navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
                            setShowSearchSuggestions(false);
                          }}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center">
                            {item.type === 'category' && <Grid className="h-4 w-4 text-gray-400 mr-3" />}
                            {item.type === 'product' && <ShoppingCart className="h-4 w-4 text-gray-400 mr-3" />}
                            {item.type === 'supplier' && <Building className="h-4 w-4 text-gray-400 mr-3" />}
                            <span className="text-sm text-gray-700">{item.name}</span>
                          </div>
                          <div>
                            {item.type === 'category' && <span className="text-xs text-gray-500">{item.count} products</span>}
                            {item.type === 'product' && <span className="text-xs text-gray-500">{item.count} items</span>}
                            {item.type === 'supplier' && item.verified && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <Check className="h-3 w-3 mr-1" />
                                Verified
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No results found for "{searchQuery}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Wishlist */}
            <div className="flex items-center space-x-2">
              <button className="p-2 text-gray-600 hover:text-orange-600 transition-colors relative">
                <Heart className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  3
                </span>
              </button>
              
              {/* Shopping Cart */}
              <Link to="/cart" className="p-2 text-gray-600 hover:text-orange-600 transition-colors relative">
                <ShoppingBag className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </Link>
            </div>

            {/* User Account */}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button 
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 text-gray-600 hover:text-orange-600 transition-colors"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden lg:inline text-sm font-medium">My Account</span>
                  <ChevronDown className="h-4 w-4 hidden lg:block" />
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b border-gray-100">
                      <div className="font-medium text-gray-900">Welcome!</div>
                      <div className="text-sm text-gray-600 truncate">{user.email}</div>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/orders"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Orders
                      </Link>
                      <Link
                        to="/my-documents"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Documents
                      </Link>
                      <Link
                        to="/wishlist"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Wishlist
                      </Link>
                      <Link
                        to="/messages"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Messages
                      </Link>
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            handleSignOut();
                            setIsUserMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/signin" className="flex items-center space-x-2 p-2 text-gray-600 hover:text-orange-600 transition-colors">
                <User className="h-5 w-5" />
                <span className="hidden lg:inline text-sm font-medium">Sign In</span>
              </Link>
            )}

            {/* Create Account */}
            {!user && (
              <Link to="/create-account" className="hidden lg:flex items-center space-x-2 bg-madeinbd-gradient text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
                <UserPlus className="h-4 w-4" />
                <span>Create Account</span>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Navigation with Categories Mega Menu */}
        <nav className="hidden md:flex items-center space-x-8 py-3 border-t border-gray-200">
          {/* Categories Mega Menu */}
          <div className="relative">
            <button
              onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
              className="flex items-center space-x-2 text-gray-700 hover:text-orange-600 font-medium transition-colors bg-orange-50 px-4 py-2 rounded-lg"
            >
              <Grid className="h-4 w-4" />
              <span>Categories</span>
              <ChevronDown className="h-3 w-3" />
            </button>
            
            {isCategoriesOpen && (
              <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">Browse Categories</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 gap-1">
                    {categories.map((category) => (
                      <div key={category.id} className="group">
                        <Link
                          to={`/category/${category.slug}`}
                          onClick={() => setIsCategoriesOpen(false)}
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-orange-50 transition-colors group"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 group-hover:text-orange-600">
                              {category.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {category.productCount.toLocaleString()} products
                            </div>
                          </div>
                          <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-orange-600" />
                        </Link>
                        
                        {/* Subcategories */}
                        <div className="ml-4 space-y-1">
                          {category.subcategories.slice(0, 4).map((subcategory) => (
                            <Link
                              key={subcategory.id}
                              to={`/category/${category.slug}?subcategory=${subcategory.slug}`}
                              onClick={() => setIsCategoriesOpen(false)}
                              className="block px-3 py-1 text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-25 rounded transition-colors"
                            >
                              {subcategory.name} ({subcategory.productCount})
                            </Link>
                          ))}
                          {category.subcategories.length > 4 && (
                            <Link
                              to={`/category/${category.slug}`}
                              onClick={() => setIsCategoriesOpen(false)}
                              className="block px-3 py-1 text-sm text-orange-600 hover:text-orange-700 font-medium"
                            >
                              View all {category.subcategories.length} subcategories →
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Link to="/" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">Home</Link>
          <Link to="/suppliers" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">Suppliers</Link>
          <Link to="/about" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">About</Link>
          <Link to="/contact" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">Contact</Link>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-4">
            {/* Mobile Search - Oval Shape with Image Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-20 py-3 border border-gray-300 rounded-full shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                <button 
                  onClick={handleImageSearch}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 p-2 rounded-full transition-colors"
                  title="Search by image"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <button className="bg-madeinbd-gradient text-white p-2 rounded-full hover:opacity-90 transition-opacity">
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Mobile Ship To & Language */}
            <div className="flex space-x-4">
              <div className="flex-1">
                <button
                  onClick={() => setIsShipToOpen(!isShipToOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Ship to {selectedCountryData.flag}</span>
                  </div>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
              <div className="flex-1">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex items-center space-x-2">
                    <Globe className="h-4 w-4" />
                    <span>{selectedLanguageData.flag} {selectedLanguageData.name}</span>
                  </div>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* Mobile User Menu */}
            {user ? (
              <div className="space-y-2">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <div className="font-medium text-gray-900">Welcome!</div>
                  <div className="text-sm text-gray-600 truncate">{user.email}</div>
                </div>
                <Link to="/profile" className="block w-full bg-orange-600 text-white py-2 px-4 rounded-lg text-center font-medium">
                  My Profile
                </Link>
                <Link to="/cart" className="block text-center py-2 px-3 bg-gray-100 rounded-lg text-gray-700 text-sm">
                  My Cart {totalItems > 0 && `(${totalItems})`}
                </Link>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/orders" className="block text-center py-2 px-3 bg-gray-100 rounded-lg text-gray-700 text-sm">
                    My Orders
                  </Link>
                  <Link to="/my-documents" className="block text-center py-2 px-3 bg-gray-100 rounded-lg text-gray-700 text-sm">
                    My Documents
                  </Link>
                  <Link to="/wishlist" className="block text-center py-2 px-3 bg-gray-100 rounded-lg text-gray-700 text-sm">
                    My Wishlist
                  </Link>
                  <Link to="/messages" className="block text-center py-2 px-3 bg-gray-100 rounded-lg text-gray-700 text-sm">
                    Messages
                  </Link>
                </div>
                <button 
                  onClick={handleSignOut}
                  className="block w-full text-center py-2 px-4 border border-gray-300 rounded-lg text-gray-700"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <Link to="/create-account" className="w-full bg-madeinbd-gradient text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2">
                <UserPlus className="h-4 w-4" />
                <span>Create Account</span>
              </Link>
            )}

            {/* Mobile Navigation */}
            <nav className="space-y-3 pt-4 border-t border-gray-200">
              <Link to="/" className="block text-gray-700 hover:text-orange-600 font-medium">Home</Link>
              
              <a href="/merchant-acquisition" className="block text-gray-700 hover:text-orange-600 font-medium" target="_self">Become Supplier</a>
              
               {/* Mobile Categories */}
              <div className="space-y-2">
                <div className="font-medium text-gray-900 text-sm uppercase tracking-wide">Categories</div>
                {categories.map((category) => (
                  <div key={category.id} className="space-y-1">
                    <Link 
                      to={`/category/${category.slug}`}
                      className="block text-gray-700 hover:text-orange-600 font-medium pl-2"
                    >
                      {category.name}
                    </Link>
                    {/* Mobile Subcategories */}
                    <div className="ml-4 space-y-1">
                      {category.subcategories.slice(0, 3).map((subcategory) => (
                        <Link
                          key={subcategory.id}
                          to={`/category/${category.slug}?subcategory=${subcategory.slug}`}
                          className="block text-sm text-gray-600 hover:text-orange-600 pl-2"
                        >
                          {subcategory.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <Link to="/suppliers" className="block text-gray-700 hover:text-orange-600 font-medium">Suppliers</Link>
              <Link to="/about" className="block text-gray-700 hover:text-orange-600 font-medium">About</Link>
              <Link to="/help" className="block text-gray-700 hover:text-orange-600 font-medium">Help Center</Link>
              <Link to="/rfq/create" className="block text-gray-700 hover:text-orange-600 font-medium">Submit RFQ</Link>
              <Link to="/rfq/create" className="text-gray-700 hover:text-orange-600 font-medium transition-colors">Submit RFQ</Link>
            </nav>
          </div>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(isShipToOpen || isLanguageOpen || isCategoriesOpen) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsShipToOpen(false);
            setIsLanguageOpen(false);
            setIsCategoriesOpen(false);
          }}
        />
      )}
    </header>
  );
};

export default Header;